import { ApiAccountRole } from 'src/Libs/api/types'
import { AccountLinkModel } from 'src/Libs/installation-rights-management/models'
import { FetchedUserDataConnected } from 'src/Libs/user/models/user.model'
import { InstallerWithAccessData } from '../models'

/**
 * Merges the installer account links of an installation with the resolved Hager user details into
 * the installer view model used by the Flow "Installers with access" section.
 *
 * Only account links of type `user` and role `installer` are included; owners, end users and
 * companies are filtered out. For Hager users the account link `accountId` is a UUID, so each
 * installer is matched to its resolved user details by username. The numeric Portal user ID,
 * contact data and profile link come from the resolved user; when an installer cannot be resolved
 * its id is `null` and its contact fields are left blank so it can still be listed by name.
 *
 * @param accountLinks - account links of the installation (any type/role)
 * @param detailsByUsername - resolved connected user details keyed by the Hager username
 * @returns installer view models with merged access rights
 */
const installersToModelTransformer = (
  accountLinks: AccountLinkModel[],
  detailsByUsername: Map<string, FetchedUserDataConnected | null>
): InstallerWithAccessData[] => {
  return accountLinks
    .filter(accountLink => accountLink.accountType === 'user' && accountLink.role === ApiAccountRole.INSTALLER)
    .map(accountLink => {
      const user = detailsByUsername.get(accountLink.userName) ?? null

      return {
        accountLinkId: accountLink.id,
        accountId: resolveAccountId(user),
        name: buildInstallerName(accountLink.firstName, accountLink.lastName, accountLink.userName),
        email: user?.['location.Email'] ?? '',
        phone: user?.['location.Telefon'] ?? '',
        street: user?.['location.PlaceOR'] ?? '',
        zip: user?.['location.PLZ'] ?? '',
        address: user?.['location.Address'] ?? '',
        countryCode: user?.['location.Land'] ?? '',
        hasReadAccess: accountLink.hasReadAccess,
        hasWriteAccess: accountLink.hasWriteAccess,
        startAt: accountLink.startAt || null,
      }
    })
}

export default installersToModelTransformer

const buildInstallerName = (firstName: string, lastName: string, userName: string): string => {
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim()

  return fullName || userName || '-'
}

const resolveAccountId = (user: FetchedUserDataConnected | null | undefined): number | null => {
  const accountId = Number(user?.['user.ID'])

  return Number.isSafeInteger(accountId) && accountId > 0 ? accountId : null
}
